<template>
  <div class="short-text-mention-list">
    <u-input
      v-model="search"
      clearable
      :placeholder="
        $t(
          'views.aiWrite.template.components.Editor.mentionList.7b6b65bda30ac5e0403ed80e13e3e503'
        )
      "
    >
      <template #suffix>
        <u-icon :size="14">
          <ULineSearch />
        </u-icon>
      </template>
    </u-input>
    <div class="mentions-select-content">
      <u-scrollbar v-if="filteredItems.length" max-height="200px">
        <div
          v-for="(item, index) in filteredItems"
          :key="`${item.value}-${index}`"
          class="select-item"
          :class="{ 'select-item-active': index === selectedIndex }"
          @click="selectItem(index)"
        >
          <div class="text">
            <OneLineText :text="item.label" />
          </div>
        </div>
      </u-scrollbar>
    </div>
    <div v-if="!filteredItems.length" class="no-more-wrap">
      {{
        $t(
          'components.RichMentions.components.MentionsSelect.8ca32e5c70ec23b34f25364f885e516d'
        )
      }}
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, watch, computed } from 'vue';
import OneLineText from '@/components/OneLineText.vue';

export default defineComponent({
  name: 'VariableMentionEditorMentionList',
  components: {
    OneLineText
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    command: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const selectedIndex = ref(0);
    const search = ref('');

    const filteredItems = computed(() => {
      if (!search.value) {
        return props.items;
      }
      const keyword = String(search.value).toLowerCase();
      return (props.items || []).filter(item => {
        return (
          String(item.label || '')
            .toLowerCase()
            .includes(keyword) ||
          String(item.value || '')
            .toLowerCase()
            .includes(keyword)
        );
      });
    });

    watch(
      filteredItems,
      items => {
        if (!items.length) {
          selectedIndex.value = 0;
          return;
        }
        if (selectedIndex.value >= items.length) {
          selectedIndex.value = 0;
        }
      },
      {
        immediate: true
      }
    );

    const selectItem = index => {
      const item = filteredItems.value[index];
      if (!item) {
        return;
      }
      props.command(item);
    };

    const onKeyDown = ({ event }) => {
      if (!filteredItems.value.length) {
        return false;
      }
      if (event.key === 'ArrowUp') {
        selectedIndex.value =
          (selectedIndex.value + filteredItems.value.length - 1) %
          filteredItems.value.length;
        return true;
      }
      if (event.key === 'ArrowDown') {
        selectedIndex.value =
          (selectedIndex.value + 1) % filteredItems.value.length;
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex.value);
        return true;
      }
      return false;
    };

    return {
      search,
      filteredItems,
      selectedIndex,
      selectItem,
      onKeyDown
    };
  }
});
</script>

<style lang="less" scoped>
.short-text-mention-list {
  width: 200px;
  overflow: hidden;
  z-index: 1000;
  color: var(--font-color);
  background: var(--color-filled-background-container-primary);
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-neutral-color-secondary);
  border-radius: 8px;
  padding: 8px;

  .select-item {
    height: 32px;
    border-radius: 6px 6px 6px 6px;
    line-height: 32px;
    padding-left: 8px;
    padding-right: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    &:hover {
      background: var(--card-background-color);
      color: var(--active-font-color);
    }
  }

  .select-item-active {
    background: var(--card-background-color);
    color: var(--active-font-color);
  }

  .text {
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex: 1;

    :deep(.one-line-text) {
      min-width: 0;
      width: auto;
      max-width: 100%;
    }
  }

  .no-more-wrap {
    padding-top: 12px;
    padding-bottom: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.mentions-select-content {
  margin-top: 12px;
}
</style>
